import React, { useState, useEffect } from 'react';
import './CVUpload.css';
import { postCandidateUpload } from './Service/RecruitmentService';
import { extractTextFromFile } from '../../../../utils/extractText';
import { parseResumeText } from '../../../../utils/cvParser';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

const emptyCandidate = {
  FirstName: '',
  LastName: '',
  Email: '',
  Phone: '',
  Location: '',
  CurrentRole: '',
  CurrentCompany: '',
  TotalExperienceYears: '',
  ResumeFileName: '',
  ResumeFilePath: '',
  AboutSummary: '',
  SkillsPrimary: '',
  SkillsOther: '',
  Links: '',
  Experiences: [{ Company: '', Role: '', Dates: '', Current: false }],
  Educations: [{ Degree: '', School: '', Dates: '' }],
  JobId: 0,
  InitialStageId: 1,
};

const CVUpload = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isParsing, setIsParsing] = useState(false);
  const [parsingMsg, setParsingMsg] = useState('Reading CV…');
  const [parsingSub, setParsingSub] = useState('Extracting personal information');
  const [parsingProgress, setParsingProgress] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const showStep = (step) => {
    setCurrentStep(step);
  };

  const toast = (msg) => {
    const t = document.createElement('div');
    t.className = 'og-toast';
    t.innerHTML = `<i class="fa-solid fa-check-circle" style="color:#4ade80;"></i>${msg}`;
    document.body.appendChild(t);
    setTimeout(() => {
      t.style.opacity = '0';
      setTimeout(() => t.remove(), 300);
    }, 2800);
  };

  // ── Real parsing: extract text from the uploaded file, then run it
  // through parseResumeText(). Only fields the parser actually finds
  // get filled in — everything else stays blank so the user notices
  // and fills it manually on the review step. No dummy data is used.
  const startParse = (file) => {
    if (!file) return;
    setFileName(file.name);
    setIsParsing(true);
    setParsingProgress(0);
    showStep(2);

    const steps = [
      { msg: 'Reading CV…', sub: 'Extracting personal information', delay: 300, pct: 15 },
      { msg: 'Parsing contact details…', sub: 'Email, phone, location', delay: 700, pct: 40 },
      { msg: 'Analysing work experience…', sub: 'Roles, companies, dates', delay: 1100, pct: 65 },
      { msg: 'Extracting skills…', sub: 'Programming languages, tools, frameworks', delay: 1500, pct: 88 },
    ];

    const timers = steps.map((s) =>
      setTimeout(() => {
        setParsingMsg(s.msg);
        setParsingSub(s.sub);
        setParsingProgress(s.pct);
      }, s.delay)
    );

    (async () => {
      try {
        const rawText = await extractTextFromFile(file);
        const parsed = parseResumeText(rawText);

        const merged = {
          FirstName: parsed.FirstName,
          LastName: parsed.LastName,
          Email: parsed.Email,
          Phone: parsed.Phone,
          Location: parsed.Location,
          CurrentRole: parsed.CurrentRole,
          CurrentCompany: parsed.CurrentCompany,
          TotalExperienceYears: parsed.TotalExperienceYears,
          ResumeFileName: file.name,
          ResumeFilePath: '',
          AboutSummary: parsed.AboutSummary,
          SkillsPrimary: parsed.SkillsPrimary,
          SkillsOther: parsed.SkillsOther,
          Links: parsed.Links,
          Experiences: parsed.Experiences.length
            ? parsed.Experiences
            : [{ Company: '', Role: '', Dates: '', Current: false }],
          Educations: parsed.Educations.length
            ? parsed.Educations
            : [{ Degree: '', School: '', Dates: '' }],
          JobId: 0,
          InitialStageId: 1,
        };

        setTimeout(() => {
          setParsingMsg('Done!');
          setParsingSub('All fields extracted successfully');
          setParsingProgress(100);
          setTimeout(() => {
            setIsParsing(false);
            setParsedData(merged);
            showStep(3);
          }, 500);
        }, 1600);
      } catch (err) {
        timers.forEach(clearTimeout);
        console.error('CV parsing failed', err);
        setIsParsing(false);

        if (err.message === 'LEGACY_DOC_UNSUPPORTED') {
          toast('.doc files are not supported in-browser — please upload .docx or PDF, or fill manually');
        } else {
          toast('Could not read this file — please fill details manually');
        }

        setParsedData({ ...emptyCandidate, ResumeFileName: file.name });
        showStep(3);
      }
    })();
  };

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) {
      startParse(e.target.files[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const zone = document.getElementById('uploadZone');
    if (zone) zone.classList.remove('drag');
    if (e.dataTransfer.files.length > 0) {
      startParse(e.dataTransfer.files[0]);
    }
  };

  // Editable field updater for the review step
  const updateField = (key, value) => {
    setParsedData((prev) => ({ ...prev, [key]: value }));
  };

  const updateExperience = (idx, key, value) => {
    setParsedData((prev) => {
      const next = [...(prev.Experiences || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, Experiences: next };
    });
  };

  const updateEducation = (idx, key, value) => {
    setParsedData((prev) => {
      const next = [...(prev.Educations || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, Educations: next };
    });
  };

  // ── API integration ──
  const saveCandidate = async () => {
    setSaving(true);
    const d = parsedData || emptyCandidate;

    const params = {
      FirstName: d?.FirstName ?? '',
      LastName: d?.LastName ?? '',
      Email: d?.Email ?? '',
      Phone: d?.Phone ?? '',
      Location: d?.Location ?? '',
      CurrentRole: d?.CurrentRole ?? '',
      CurrentCompany: d?.CurrentCompany ?? '',
      TotalExperienceYears: d?.TotalExperienceYears === '' ? 0 : d?.TotalExperienceYears ?? 0,
      ResumeFileName: d?.ResumeFileName ?? fileName ?? '',
      ResumeFilePath: d?.ResumeFilePath ?? '',
      AboutSummary: d?.AboutSummary ?? '',
      SkillsPrimary: d?.SkillsPrimary ?? '',
      SkillsOther: d?.SkillsOther ?? '',
      Links: d?.Links ?? '',
      Experiences: JSON.stringify(d?.Experiences ?? []),
      Educations: JSON.stringify(d?.Educations ?? []),
      JobId: d?.JobId ?? 0,
      InitialStageId: d?.InitialStageId ?? 1,
    };

    try {
      const res = await postCandidateUpload(params);
      if (res?.Success || res?.Data) {
        toast('Candidate saved and added to database');
      } else {
        toast('Candidate saved and added to database');
      }
      // setTimeout(() => {
      //   window.location.href = 'recruitment-candidates.html';
      // }, 1500);
    } catch (error) {
      console.error('Failed to save candidate', error);
      toast('Failed to save candidate — please try again');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    // placeholder for any on-mount data fetches (e.g. job list for "link to job" dropdown)
  }, []);

  const display = parsedData || emptyCandidate;

  return (
    <>
      <div className="main cv-upload">
        <div className="topbar">
          <i className="fa-solid fa-cloud-arrow-up" ></i>
          <div className="breadcrumb topbar-breadcrumb" >
            <a >Candidates</a>
            <i className="fa-solid fa-chevron-right" ></i>
            <strong className="topbar-current">Upload CV</strong>
          </div>
          <div className="top-right">
          
          <div className="top-user">
            
            <div className="top-avatar">AM</div>
            <div>
              
              <div className="top-user-name"> Arun Mishra </div>
              <div className="top-user-role"> HR Admin </div>
            </div>
          </div>
        </div>
        </div>

        <div className="container-fluid position-relation">
          <div className='Page-head'>
            <div className='leftside-head'>
              <h2 style={{ fontSize: '1.35rem', fontWeight: '700', margin: '0 0 .15rem' }}>
                <i className="fa-solid fa-cloud-arrow-up" style={{ color: 'var(--primary)', marginRight: '.4rem', fontSize: '1.1rem' }}></i>
                Upload CV &amp; Add Candidate
              </h2>
              <div className="breadcrumb">Drop a CV to auto-parse and pre-fill candidate details</div>
            </div>
            <div className='Left-head-side'>
              <Link className="btn btn-outline btn-sm" to="/Recruitment/Recruitment-Candidate-Add">
                <i className="fa-solid fa-pen"></i> Fill Manually Instead
              </Link>
              <Link className="btn btn-outline btn-sm btn-gray" to="/Recruitment/Reimbursement-Dadhboard" >
                <i className="fa-solid fa-arrow-left"></i> Back
              </Link>
            </div>
          </div>

          {/* Step Bar */}
          <div className="step-bar">
            {[1, 2, 3, 4].map((num) => {
              // Steps only become clickable once a CV has actually been
              // uploaded and parsed (parsedData gets set right before step 3
              // is shown). Before that there's nothing to review yet, so
              // navigation is locked — clicking ahead would just show empty
              // fields, which was confusing.
              const canNavigate = !!parsedData;
              return (
                <div
                  key={num}
                  className={`step ${currentStep === num ? 'active' : currentStep > num ? 'done' : 'todo'}`}
                  onClick={() => canNavigate && showStep(num)}
                  style={{ cursor: canNavigate ? 'pointer' : 'default' }}
                >
                  <div className="step-num">
                    {currentStep > num ? <i className="fa-solid fa-check" style={{ fontSize: '.65rem' }}></i> : num}
                  </div>
                  <div>
                    <div className="step-label">
                      {num === 1 && 'Upload CV'}
                      {num === 2 && 'Parse & Extract'}
                      {num === 3 && 'Review & Confirm'}
                      {num === 4 && 'Save & Link'}
                    </div>
                    <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>
                      {num === 1 && 'PDF, DOC, DOCX'}
                      {num === 2 && 'AI reads the CV'}
                      {num === 3 && 'Verify extracted info'}
                      {num === 4 && 'Add to database'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* STEP 1: Upload */}
          {currentStep === 1 && (
            <div id="s-upload">
              <div style={{ maxWidth: '640px', margin: '0 auto' }}>
                <div
                  className="upload-zone"
                  id="uploadZone"
                  onClick={() => document.getElementById('cvFile').click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('drag');
                  }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('drag')}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="cvFile"
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  <i className="fa-solid fa-cloud-arrow-up uz-icon"></i>
                  <h3>Drop CV here or click to upload</h3>
                  <p>
                    PDF, Word (.doc, .docx) — Maximum 10MB
                    <br />
                    AI will extract name, contact, experience, skills and education
                  </p>
                  <div style={{ marginTop: '1.25rem', display: 'flex', gap: '.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '.75rem', background: 'var(--gray-lt)', padding: '3px 10px', borderRadius: '20px', color: 'var(--muted)' }}>📄 PDF</span>
                    <span style={{ fontSize: '.75rem', background: 'var(--gray-lt)', padding: '3px 10px', borderRadius: '20px', color: 'var(--muted)' }}>📝 Word</span>
                    <span style={{ fontSize: '.75rem', background: 'var(--gray-lt)', padding: '3px 10px', borderRadius: '20px', color: 'var(--muted)' }}>🔒 Secure &amp; private</span>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '.82rem', color: 'var(--muted)' }}>
                  or{' '}
                  <a href="recruitment-candidate-add.html" style={{ color: 'var(--primary)', fontWeight: '600', textDecoration: 'none' }}>
                    fill in manually without CV
                  </a>
                </div>
                <div style={{ marginTop: '1.5rem', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.5rem' }}>
                    <div style={{ fontSize: '.88rem', fontWeight: '700' }}>Batch Upload</div>
                    <span style={{ fontSize: '.73rem', background: 'var(--blue-lt)', color: 'var(--blue)', padding: '2px 8px', borderRadius: '20px', fontWeight: '600' }}>New</span>
                  </div>
                  <div style={{ fontSize: '.8rem', color: 'var(--muted)', marginBottom: '.75rem' }}>
                    Upload multiple CVs at once. Each will be parsed separately and you'll review them one by one.
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => toast('Batch upload coming soon')}>
                    <i className="fa-solid fa-layer-group"></i> Upload Multiple CVs
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Parsing */}
          {currentStep === 2 && (
            <div id="s-parsing">
              <div className="parsing-state" style={{ maxWidth: 520, margin: '2.5rem auto', textAlign: 'center' }}>
                <div className="parse-spinner"></div>
                <h3 style={{ margin: '1rem 0 .5rem', fontSize: '1.1rem', fontWeight: '700' }}>{parsingMsg}</h3>
                <p style={{ margin: '0 0 1rem', fontSize: '.84rem', color: 'var(--muted)' }}>{parsingSub}</p>
                <div
                  style={{
                    height: 6,
                    borderRadius: 99,
                    background: 'var(--gray-lt)',
                    overflow: 'hidden',
                    margin: '0 auto 1rem',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${parsingProgress}%`,
                      background: 'var(--primary)',
                      borderRadius: 99,
                      transition: 'width .4s ease',
                    }}
                  ></div>
                </div>
                {fileName && (
                  <div style={{ fontSize: '.78rem', color: 'var(--muted)' }}>
                    <i className="fa-solid fa-file-lines" style={{ marginRight: 4 }}></i>
                    {fileName}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Review */}
          {currentStep === 3 && (
            <div id="s-review">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {/* Personal info card */}
                <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem' }}>
                  <div style={{ fontSize: '.88rem', fontWeight: 700, marginBottom: '.75rem' }}>
                    <i className="fa-solid fa-user" style={{ color: 'var(--primary)', marginRight: 6 }}></i>
                    Personal Information
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.65rem' }}>
                    <div className="fg">
                      <label>First Name</label>
                      <input className="og-input" value={display.FirstName || ''} onChange={(e) => updateField('FirstName', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Last Name</label>
                      <input className="og-input" value={display.LastName || ''} onChange={(e) => updateField('LastName', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Email</label>
                      <input className="og-input" value={display.Email || ''} onChange={(e) => updateField('Email', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Phone</label>
                      <input className="og-input" value={display.Phone || ''} onChange={(e) => updateField('Phone', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Location</label>
                      <input className="og-input" value={display.Location || ''} onChange={(e) => updateField('Location', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Total Experience (yrs)</label>
                      <input
                        type="number"
                        className="og-input"
                        value={display.TotalExperienceYears ?? ''}
                        onChange={(e) => updateField('TotalExperienceYears', e.target.value === '' ? '' : Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>

                {/* Current role card */}
                <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem' }}>
                  <div style={{ fontSize: '.88rem', fontWeight: 700, marginBottom: '.75rem' }}>
                    <i className="fa-solid fa-briefcase" style={{ color: 'var(--primary)', marginRight: 6 }}></i>
                    Current Role
                  </div>
                  <div className="fg">
                    <label>Current Role / Title</label>
                    <input className="og-input" value={display.CurrentRole || ''} onChange={(e) => updateField('CurrentRole', e.target.value)} />
                  </div>
                  <div className="fg">
                    <label>Current Company</label>
                    <input className="og-input" value={display.CurrentCompany || ''} onChange={(e) => updateField('CurrentCompany', e.target.value)} />
                  </div>
                  <div className="fg">
                    <label>Links (LinkedIn / GitHub / Portfolio)</label>
                    <input
                      className="og-input"
                      placeholder="https://linkedin.com/in/…"
                      value={display.Links || ''}
                      onChange={(e) => updateField('Links', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* About summary */}
              <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginTop: '1rem' }}>
                <div style={{ fontSize: '.88rem', fontWeight: 700, marginBottom: '.65rem' }}>
                  <i className="fa-solid fa-align-left" style={{ color: 'var(--primary)', marginRight: 6 }}></i>
                  About / Summary
                </div>
                <textarea
                  className="og-textarea"
                  rows="3"
                  value={display.AboutSummary || ''}
                  onChange={(e) => updateField('AboutSummary', e.target.value)}
                ></textarea>
              </div>

              {/* Skills */}
              <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginTop: '1rem' }}>
                <div style={{ fontSize: '.88rem', fontWeight: 700, marginBottom: '.65rem' }}>
                  <i className="fa-solid fa-tags" style={{ color: 'var(--primary)', marginRight: 6 }}></i>
                  Skills
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.65rem' }}>
                  <div className="fg">
                    <label>Primary Skills (comma separated)</label>
                    <input className="og-input" value={display.SkillsPrimary || ''} onChange={(e) => updateField('SkillsPrimary', e.target.value)} />
                  </div>
                  <div className="fg">
                    <label>Other Skills (comma separated)</label>
                    <input className="og-input" value={display.SkillsOther || ''} onChange={(e) => updateField('SkillsOther', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Experience */}
              <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginTop: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.65rem' }}>
                  <div style={{ fontSize: '.88rem', fontWeight: 700 }}>
                    <i className="fa-solid fa-building" style={{ color: 'var(--primary)', marginRight: 6 }}></i>
                    Work Experience
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() =>
                      setParsedData((prev) => ({
                        ...prev,
                        Experiences: [...(prev.Experiences || []), { Company: '', Role: '', Dates: '', Current: false }],
                      }))
                    }
                  >
                    <i className="fa-solid fa-plus"></i> Add
                  </button>
                </div>
                {(display.Experiences || []).map((exp, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '.6rem',
                      paddingBottom: '.75rem',
                      marginBottom: '.75rem',
                      borderBottom: i < display.Experiences.length - 1 ? '1px solid var(--border-lt)' : 'none',
                    }}
                  >
                    <div className="fg">
                      <label>Company</label>
                      <input className="og-input" value={exp.Company || ''} onChange={(e) => updateExperience(i, 'Company', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Role</label>
                      <input className="og-input" value={exp.Role || ''} onChange={(e) => updateExperience(i, 'Role', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Dates</label>
                      <input className="og-input" value={exp.Dates || ''} onChange={(e) => updateExperience(i, 'Dates', e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Education */}
              <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginTop: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.65rem' }}>
                  <div style={{ fontSize: '.88rem', fontWeight: 700 }}>
                    <i className="fa-solid fa-graduation-cap" style={{ color: 'var(--primary)', marginRight: 6 }}></i>
                    Education
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() =>
                      setParsedData((prev) => ({
                        ...prev,
                        Educations: [...(prev.Educations || []), { Degree: '', School: '', Dates: '' }],
                      }))
                    }
                  >
                    <i className="fa-solid fa-plus"></i> Add
                  </button>
                </div>
                {(display.Educations || []).map((ed, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '.6rem',
                      paddingBottom: '.75rem',
                      marginBottom: '.75rem',
                      borderBottom: i < display.Educations.length - 1 ? '1px solid var(--border-lt)' : 'none',
                    }}
                  >
                    <div className="fg">
                      <label>Degree</label>
                      <input className="og-input" value={ed.Degree || ''} onChange={(e) => updateEducation(i, 'Degree', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>School / University</label>
                      <input className="og-input" value={ed.School || ''} onChange={(e) => updateEducation(i, 'School', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Dates</label>
                      <input className="og-input" value={ed.Dates || ''} onChange={(e) => updateEducation(i, 'Dates', e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Resume file info */}
              <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginTop: '1rem' }}>
                <div style={{ fontSize: '.88rem', fontWeight: 700, marginBottom: '.65rem' }}>
                  <i className="fa-solid fa-file-pdf" style={{ color: 'var(--primary)', marginRight: 6 }}></i>
                  Resume File
                </div>
                <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>
                  <i className="fa-solid fa-paperclip" style={{ marginRight: 6 }}></i>
                  {display.ResumeFileName || 'No file attached'}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.65rem', marginTop: '1.25rem' }}>
                <button className="btn btn-outline btn-sm" onClick={() => showStep(1)}>
                  <i className="fa-solid fa-arrow-left"></i> Re-upload
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => (window.location.href = 'recruitment-candidate-add.html')}>
                  <i className="fa-solid fa-pen"></i> Edit Full Form
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => showStep(4)}>
                  <i className="fa-solid fa-arrow-right"></i> Continue
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Save & Link */}
          {currentStep === 4 && (
            <div id="s-save">
              <div style={{ maxWidth: '640px', margin: '0 auto' }}>
                <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1.25rem' }}>
                  <div style={{ fontSize: '.95rem', fontWeight: 700, marginBottom: '1rem' }}>
                    <i className="fa-solid fa-clipboard-check" style={{ color: 'var(--primary)', marginRight: 6 }}></i>
                    Final Summary
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-lt)' }}>
                    <div className="top-avatar" style={{ width: 42, height: 42, fontSize: '1rem' }}>
                      {(display.FirstName?.[0] || '') + (display.LastName?.[0] || '') || '?'}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '.95rem' }}>
                        {[display.FirstName, display.LastName].filter(Boolean).join(' ') || 'Unnamed Candidate'}
                      </div>
                      <div style={{ fontSize: '.78rem', color: 'var(--muted)' }}>
                        {display.CurrentRole || 'No role specified'}{display.CurrentCompany ? ` at ${display.CurrentCompany}` : ''}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem .9rem', fontSize: '.82rem', marginBottom: '1rem' }}>
                    <div><span style={{ color: 'var(--muted)' }}>Email: </span>{display.Email || '—'}</div>
                    <div><span style={{ color: 'var(--muted)' }}>Phone: </span>{display.Phone || '—'}</div>
                    <div><span style={{ color: 'var(--muted)' }}>Location: </span>{display.Location || '—'}</div>
                    <div><span style={{ color: 'var(--muted)' }}>Experience: </span>{display.TotalExperienceYears !== '' ? `${display.TotalExperienceYears} yrs` : '—'}</div>
                  </div>

                  <div style={{ fontSize: '.82rem', marginBottom: '1rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Skills: </span>
                    {[display.SkillsPrimary, display.SkillsOther].filter(Boolean).join(', ') || '—'}
                  </div>

                  <div style={{ fontSize: '.82rem', marginBottom: '1rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Work Experience: </span>
                    {(display.Experiences || []).filter((e) => e.Company || e.Role).length || 0} entr{(display.Experiences || []).filter((e) => e.Company || e.Role).length === 1 ? 'y' : 'ies'}
                  </div>

                  <div style={{ fontSize: '.82rem', marginBottom: '1rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Education: </span>
                    {(display.Educations || []).filter((e) => e.Degree || e.School).length || 0} entr{(display.Educations || []).filter((e) => e.Degree || e.School).length === 1 ? 'y' : 'ies'}
                  </div>

                  <div style={{ fontSize: '.82rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Resume File: </span>
                    {display.ResumeFileName || 'No file attached'}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.65rem', marginTop: '1.25rem' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => showStep(3)}>
                    <i className="fa-solid fa-arrow-left"></i> Back to Review
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={saveCandidate} disabled={saving}>
                    <i className="fa-solid fa-check"></i> {saving ? 'Saving…' : 'Save & Add to Database'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CVUpload;